"use client";

import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import {
	brazilianCalculatorHref,
	getBrazilLanguageSignal,
	shouldSuggestBrazilianPortuguese,
} from "@/lib/geo/brazil-language-suggestion";
import { GEO_PREF_UPDATED_EVENT, parseGeoPrefCookieValue } from "@/lib/geo/geo-pref";
import { readGeoPrefCookieRaw, setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { prepareCalculatorLocaleTransition } from "@/lib/calculator/locale-transition";
import type { CalculatorState } from "@/lib/calculator/use-calculator-state";
import { Button } from "@/ui/components/ui/button";
import { useLocaleChannelState } from "./use-locale-channel-state";

const DISMISS_COOKIE = "bb_br_language_dismissed";
const DISMISS_EVENT = "bergdorf-br-language-dismissed";

function subscribe(onChange: () => void) {
	window.addEventListener(GEO_PREF_UPDATED_EVENT, onChange);
	window.addEventListener(DISMISS_EVENT, onChange);
	window.addEventListener("focus", onChange);
	window.addEventListener("languagechange", onChange);
	return () => {
		window.removeEventListener(GEO_PREF_UPDATED_EVENT, onChange);
		window.removeEventListener(DISMISS_EVENT, onChange);
		window.removeEventListener("focus", onChange);
		window.removeEventListener("languagechange", onChange);
	};
}

function browserLanguage() {
	return navigator.languages?.[0] ?? navigator.language;
}

function dismissed() {
	return document.cookie.split(";").some((part) => part.trim() === `${DISMISS_COOKIE}=1`);
}

/** Browser-only, voluntary prompt. Calculator values never enter its analytics payload. */
export function BrazilLanguageSuggestion({ calculatorState }: { calculatorState: CalculatorState }) {
	const t = useTranslations("localeChannel.brazilSuggestion");
	const context = useLocaleChannelState();
	const pathname = usePathname();
	const router = useRouter();
	const titleId = useId();
	const regionRef = useRef<HTMLElement>(null);
	const trackedPath = useRef<string | null>(null);
	const [hidden, setHidden] = useState(false);
	const getSnapshot = useCallback(
		() =>
			shouldSuggestBrazilianPortuguese({
				pathname,
				detectedCountry: context.detectedCountry,
				preferredBrowserLanguage: browserLanguage(),
				savedLocale: parseGeoPrefCookieValue(readGeoPrefCookieRaw())?.locale ?? null,
				dismissed: dismissed(),
			}),
		[pathname, context.detectedCountry],
	);
	const eligible = useSyncExternalStore(subscribe, getSnapshot, () => false);
	const visible = eligible && !hidden;

	const capture = useCallback(
		(
			event:
				| "brazil_language_suggestion_viewed"
				| "brazil_language_suggestion_accepted"
				| "brazil_language_suggestion_dismissed",
		) => {
			try {
				posthog.capture(event, {
					event_schema_version: 1,
					surface: "peptide_calculator",
					from_locale: "pt",
					to_locale: "pt-br",
					channel: context.currentChannel,
					signal: getBrazilLanguageSignal(context.detectedCountry, browserLanguage()),
				});
			} catch {
				// Analytics availability must never affect a language choice.
			}
		},
		[context.currentChannel, context.detectedCountry],
	);

	useEffect(() => {
		if (!visible || !regionRef.current || trackedPath.current === pathname) return;
		const track = () => {
			if (trackedPath.current === pathname) return;
			trackedPath.current = pathname;
			capture("brazil_language_suggestion_viewed");
		};
		if (typeof IntersectionObserver === "undefined") return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					track();
					observer.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		observer.observe(regionRef.current);
		return () => observer.disconnect();
	}, [visible, pathname, capture]);

	if (!visible) return null;

	const accept = () => {
		const href = brazilianCalculatorHref(window.location);
		if (!href) return;
		prepareCalculatorLocaleTransition(
			window.location.pathname.replace(/^\/pt\//, "/pt-br/"),
			calculatorState,
		);
		capture("brazil_language_suggestion_accepted");
		setGeoPrefCookie({ locale: "pt-br", channel: context.currentChannel });
		setHidden(true);
		router.push(href, { scroll: false });
	};

	const dismiss = () => {
		capture("brazil_language_suggestion_dismissed");
		document.cookie = `${DISMISS_COOKIE}=1; Path=/; Max-Age=${30 * 86400}; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
		setHidden(true);
		window.dispatchEvent(new Event(DISMISS_EVENT));
	};

	return (
		<aside
			ref={regionRef}
			aria-labelledby={titleId}
			className="mb-5 rounded-xl border border-border bg-muted-soft p-4"
		>
			<p id={titleId} className="text-sm font-semibold text-foreground">
				{t("title")}
			</p>
			<p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t("description")}</p>
			<div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
				<Button type="button" onClick={accept} className="h-auto min-h-11 whitespace-normal text-center">
					{t("accept")}
				</Button>
				<Button
					type="button"
					variant="ghost"
					onClick={dismiss}
					className="h-auto min-h-11 whitespace-normal text-center"
				>
					{t("dismiss")}
				</Button>
			</div>
		</aside>
	);
}
